import { useState, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, Polygon, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { X, Undo2, Eraser, Check } from "lucide-react";
import ModalPortal from "@/components/ui/modal-portal";
import { DRAW_LAYER, MAX_ZOOM } from "@/lib/mapLayers";

const POINT_ICON = L.divIcon({
  className: "",
  html: '<div style="width:10px;height:10px;border-radius:50%;background:#2f6a92;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>',
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

function ClickCatcher({ onClick }) {
  useMapEvents({ click: (e) => onClick({ lat: e.latlng.lat, lon: e.latlng.lng }) });
  return null;
}

/** Même calcul que le serveur (real_data_fetcher.compute_area_ha_from_coords) : la surface
 *  affichée pendant le tracé est celle qui sera enregistrée. */
function areaHa(points) {
  if (points.length < 3) return null;
  const ring = [...points.map((p) => [p.lon, p.lat]), [points[0].lon, points[0].lat]];
  const n = ring.length;
  let area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += ring[i][0] * ring[j][1] - ring[j][0] * ring[i][1];
  }
  area = Math.abs(area) / 2;
  const avgLat = ring.reduce((s, c) => s + c[1], 0) / n;
  const latM = 111000;
  const lonM = 111000 * Math.cos((Math.abs(avgLat) * Math.PI) / 180);
  return (area * latM * lonM) / 10000;
}

const fmt = (v, d) => v.toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d });

function boundsFromGeometry(geometry) {
  let coords = [];
  if (geometry?.type === "Polygon" && geometry.coordinates?.[0]) coords = geometry.coordinates[0];
  else if (geometry?.type === "MultiPolygon" && geometry.coordinates?.[0]?.[0]) coords = geometry.coordinates[0][0];
  if (!coords.length) return null;
  const lats = coords.map((c) => c[1]);
  const lngs = coords.map((c) => c[0]);
  return [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
}

/**
 * Tracé d'un îlot sur la carte (phase 2) : la parcelle est affichée en vert,
 * les autres îlots en pointillé gris ; chaque clic pose un sommet du polygone.
 */
export default function IlotDrawModal({ parcel, otherIlots = [], initialPoints = [], onDone, onClose }) {
  const [points, setPoints] = useState(initialPoints);
  const bounds = useMemo(() => boundsFromGeometry(parcel?.geometry), [parcel]);
  const surface = areaHa(points);
  const parcelHa = parcel?.surface_ha;
  const tooBig = surface != null && parcelHa && surface > parcelHa * 1.02;
  const movePoint = (i, latlng) => setPoints((prev) => prev.map((p, j) => (j === i ? { lat: latlng.lat, lon: latlng.lng } : p)));

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[2200] bg-black/60 flex items-center justify-center p-3" data-testid="ilot-draw-modal">
      <div className="bg-white rounded-2xl overflow-hidden w-full max-w-3xl h-[80dvh] flex flex-col shadow-2xl">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-black/10">
          <p className="text-sm font-bold shrink-0" style={{ color: "#1A1A1A" }}>Tracer l'îlot</p>
          <p className="text-[11px] text-gray-500 flex-1 min-w-0">Cliquez sur la carte pour poser les sommets (3 minimum), à l'intérieur de la parcelle. Faites glisser un point pour le déplacer : la surface se met à jour en direct.</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5 shrink-0" data-testid="ilot-draw-close" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 relative">
          {bounds && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] rounded-xl bg-white/95 shadow-lg px-4 py-2 text-center pointer-events-none" data-testid="ilot-draw-surface">
              {surface == null ? (
                <p className="text-[12px] text-gray-500">Surface : posez au moins 3 points</p>
              ) : (
                <>
                  <p className="text-[18px] font-bold leading-tight tabular-nums" style={{ color: tooBig ? "#B4532F" : "#2f6a92" }}>
                    {fmt(surface, 2)} ha
                  </p>
                  <p className="text-[11px] text-gray-500 tabular-nums">
                    {fmt(Math.round(surface * 10000), 0)} m²{parcelHa ? ` · parcelle ${fmt(parcelHa, 2)} ha` : ""}
                  </p>
                  {tooBig && <p className="text-[11px] font-semibold" style={{ color: "#B4532F" }}>Plus grand que la parcelle</p>}
                </>
              )}
            </div>
          )}
          {bounds ? (
            <MapContainer bounds={bounds} maxZoom={MAX_ZOOM} className="h-full w-full">
              <TileLayer url={DRAW_LAYER.url} attribution={DRAW_LAYER.attribution} maxZoom={MAX_ZOOM} maxNativeZoom={DRAW_LAYER.maxNativeZoom} />
              <ClickCatcher onClick={(p) => setPoints((prev) => [...prev, p])} />
              {parcel?.geometry && (
                <GeoJSON data={{ type: "Feature", geometry: parcel.geometry }} style={{ color: "#2D6A4F", weight: 2.5, fillOpacity: 0.05 }} />
              )}
              {otherIlots.filter((i) => i.geometry).map((i) => (
                <GeoJSON key={i.ilot_id} data={{ type: "Feature", geometry: i.geometry }} style={{ color: "#7A7A7A", weight: 1.5, dashArray: "4 3", fillOpacity: 0.08 }} />
              ))}
              {points.length >= 2 && (
                <Polygon positions={points.map((p) => [p.lat, p.lon])} pathOptions={{ color: "#2f6a92", weight: 2, fillColor: "#2f6a92", fillOpacity: 0.25 }} />
              )}
              {points.map((p, i) => (
                <Marker
                  key={i}
                  position={[p.lat, p.lon]}
                  icon={POINT_ICON}
                  draggable
                  eventHandlers={{ drag: (e) => movePoint(i, e.target.getLatLng()), dragend: (e) => movePoint(i, e.target.getLatLng()) }}
                />
              ))}
            </MapContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-gray-500 px-6 text-center">
              Géométrie de la parcelle indisponible — le tracé nécessite une parcelle avec contour.
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 px-4 py-2.5 border-t border-black/10">
          <span className="text-[11px] text-gray-500 tabular-nums">
            {points.length} point{points.length > 1 ? "s" : ""}{surface != null ? ` · ${fmt(surface, 2)} ha` : ""}
          </span>
          <button
            onClick={() => setPoints((p) => p.slice(0, -1))}
            disabled={!points.length}
            className="ml-auto h-8 px-2.5 text-xs border border-black/15 rounded-md flex items-center gap-1 disabled:opacity-40"
            data-testid="ilot-draw-undo"
          >
            <Undo2 className="w-3.5 h-3.5" /> Annuler le dernier
          </button>
          <button
            onClick={() => setPoints([])}
            disabled={!points.length}
            className="h-8 px-2.5 text-xs border border-black/15 rounded-md flex items-center gap-1 disabled:opacity-40"
            data-testid="ilot-draw-clear"
          >
            <Eraser className="w-3.5 h-3.5" /> Effacer
          </button>
          <button
            onClick={() => onDone(points)}
            disabled={points.length < 3}
            className="h-8 px-3 text-xs font-semibold rounded-md text-white flex items-center gap-1 disabled:opacity-40"
            style={{ background: "#2D6A4F" }}
            data-testid="ilot-draw-done"
          >
            <Check className="w-3.5 h-3.5" /> Valider le tracé
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
